import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { useMemo } from "react";

import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { scrollToElementById } from "@/utils/scroll";

import { getSortedFeatureKinds, hasDocuments } from "../helpers";

type NavigationProps = {
    product: Product<ProductFieldSet.Full>;
};

const Navigation = ({ product }: NavigationProps) => {
    const { t } = useGranite();

    const hasFeaturesResult = useMemo(() => {
        return (
            getSortedFeatureKinds(product, t)
                .filter((f) => !!f.name)
                .map((f) => f.name).length > 0
        );
    }, [product, t]);

    const hasDocumentsResult = useMemo(() => {
        return hasDocuments(product);
    }, [product]);

    const handleOnDescriptionClick = () => {
        scrollToElementById(t("productDetail.anchor.hash.description"), 30);
    };

    const handleOnFeaturesClick = () => {
        scrollToElementById(t("productDetail.anchor.hash.features"), 30);
    };

    const handleOnDocumentationClick = () => {
        scrollToElementById(t("productDetail.anchor.hash.documentation"), 30);
    };

    return (
        <nav
            className={cn(
                "tw:bg-background tw:layer-base/[1] tw:border-b tw:border-b-grey-light tw:border-solid tw:sticky tw:top-0 tw:my-6 tw:hidden! tw:lg:flex! tw:gap-8"
            )}
        >
            <Button
                color={"link"}
                className={cn("tw:block tw:py-2 tw:px-0 js-anchor-scroll-spy")}
                onClick={handleOnDescriptionClick}
            >
                {t("productDetail.anchor.title.description")}
            </Button>
            {hasFeaturesResult && (
                <Button
                    color={"link"}
                    className={cn(
                        "tw:block tw:py-2 tw:px-0 js-anchor-scroll-spy"
                    )}
                    onClick={handleOnFeaturesClick}
                >
                    {t("productDetail.anchor.title.features")}
                </Button>
            )}
            {hasDocumentsResult && (
                <Button
                    color={"link"}
                    className={cn(
                        "tw:block tw:py-2 tw:px-0 js-anchor-scroll-spy"
                    )}
                    onClick={handleOnDocumentationClick}
                >
                    {t("productDetail.anchor.title.documentation")}
                </Button>
            )}
        </nav>
    );
};

export { Navigation };
